import React, { useEffect, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import AdminLayout from './AdminLayout';
import supabase from '../../supabase/supabase';

const { FiCopy, FiPlus, FiRefreshCw, FiSave, FiX, FiKey, FiEdit2, FiTrash2 } = FiIcons;
const emptyForm = { name: '', email: '', promo_code: '', discount_percent: '5', portal_password: '' };
const emptyPayout = { amount: '', payout_type: 'monthly', notes: '' };

function makeToken() {
  return `${crypto.randomUUID().replaceAll('-', '')}${Date.now().toString(36)}`;
}

function generatePassword() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
  let password = '';
  for (let i = 0; i < 12; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return password;
}

function AdminInfluencers() {
  const currentMonth = new Date().toISOString().slice(0, 7);
  const today = new Date();
  const [activeTab, setActiveTab] = useState('list');
  const [influencers, setInfluencers] = useState([]);
  const [sales, setSales] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [payoutMonth, setPayoutMonth] = useState(currentMonth);
  const [payoutTarget, setPayoutTarget] = useState(null);
  const [payoutForm, setPayoutForm] = useState(emptyPayout);
  const [payoutMessage, setPayoutMessage] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', promo_code: '', discount_percent: '', portal_password: '' });

  const load = async () => {
    const [{ data: influencerData }, { data: salesData }, { data: payoutData }] = await Promise.all([
      supabase.from('influencers_1750000011').select('*').order('created_at', { ascending: false }),
      supabase.from('influencer_sales_1750000011').select('*'),
      supabase.from('influencer_payouts_1750000016').select('*').order('paid_at', { ascending: false })
    ]);
    setInfluencers(influencerData || []);
    setSales(salesData || []);
    setPayouts(payoutData || []);
  };

  useEffect(() => { load(); }, []);

  const addInfluencer = async () => {
    const cleanPassword = form.portal_password.trim();
    if (!form.name || !form.promo_code || !cleanPassword) return;
    await supabase.from('influencers_1750000011').insert({
      ...form,
      portal_password: cleanPassword,
      promo_code: form.promo_code.trim().toUpperCase(),
      discount_percent: Number(form.discount_percent),
      portal_token: makeToken()
    });
    setForm(emptyForm);
    setShowAdd(false);
    load();
  };

  const toggleActive = async (influencer) => {
    await supabase.from('influencers_1750000011').update({ active: !influencer.active }).eq('id', influencer.id);
    load();
  };

  const deleteInfluencer = async (id) => {
    if (!confirm('Delete this influencer? This will also remove their sales records.')) return;
    await supabase.from('influencer_sales_1750000011').delete().eq('influencer_id', id);
    await supabase.from('influencers_1750000011').delete().eq('id', id);
    setInfluencers((prev) => prev.filter((i) => i.id !== id));
    setSelectedIds((prev) => { const n = new Set(prev); n.delete(id); return n; });
  };

  const batchDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`Delete ${selectedIds.size} influencer(s)? This will also remove their sales records.`)) return;
    const ids = [...selectedIds];
    for (const id of ids) {
      await supabase.from('influencer_sales_1750000011').delete().eq('influencer_id', id);
      await supabase.from('influencers_1750000011').delete().eq('id', id);
    }
    setSelectedIds(new Set());
    load();
  };

  const startEdit = (influencer) => {
    setEditingId(influencer.id);
    setEditForm({
      name: influencer.name,
      email: influencer.email,
      promo_code: influencer.promo_code,
      discount_percent: String(influencer.discount_percent),
      portal_password: influencer.portal_password
    });
  };

  const saveEdit = async (id) => {
    const cleanPassword = editForm.portal_password.trim();
    if (!cleanPassword) return;
    await supabase.from('influencers_1750000011').update({
      name: editForm.name,
      email: editForm.email,
      promo_code: editForm.promo_code.trim().toUpperCase(),
      discount_percent: Number(editForm.discount_percent),
      portal_password: cleanPassword,
      updated_at: new Date().toISOString()
    }).eq('id', id);
    setEditingId(null);
    load();
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === influencers.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(influencers.map((i) => i.id)));
    }
  };

  const copy = (text) => navigator.clipboard.writeText(text);

  const copyAccessDetails = async (influencer, portal) => {
    await copy(`Access Link: ${portal}\nPassword: ${influencer.portal_password.trim()}`);
    setCopiedId(influencer.id);
    window.setTimeout(() => setCopiedId(null), 1800);
  };

  const monthSalesFor = (id) => sales.filter((sale) => (
    sale.influencer_id === id && sale.created_at?.slice(0, 7) === payoutMonth
  ));

  const monthPayoutsFor = (id) => payouts.filter((payout) => (
    payout.influencer_id === id && payout.payout_month?.slice(0, 7) === payoutMonth
  ));

  const payoutStatsFor = (id) => {
    const credited = monthSalesFor(id);
    const commission = credited.reduce((sum, sale) => sum + Number(sale.commission_amount), 0);
    const paid = monthPayoutsFor(id).reduce((sum, payout) => sum + Number(payout.amount), 0);
    return {
      revenue: credited.reduce((sum, sale) => sum + Number(sale.sale_amount), 0),
      commission,
      paid,
      balance: Math.max(commission - paid, 0)
    };
  };

  const savePayout = async () => {
    const amount = Number(payoutForm.amount);
    const available = payoutStatsFor(payoutTarget.id).balance;
    setPayoutMessage('');
    if (!amount || amount <= 0) {
      setPayoutMessage('Enter a valid payout amount.');
      return;
    }
    if (amount > available) {
      setPayoutMessage('The payout cannot be greater than the available monthly balance.');
      return;
    }
    const { error } = await supabase.from('influencer_payouts_1750000016').insert({
      influencer_id: payoutTarget.id,
      amount,
      payout_month: `${payoutMonth}-01`,
      payout_type: payoutForm.payout_type,
      notes: payoutForm.notes
    });
    if (error) {
      setPayoutMessage(error.message);
      return;
    }
    setPayoutTarget(null);
    setPayoutForm(emptyPayout);
    load();
  };

  const statsFor = (id) => {
    const credited = sales.filter((sale) => sale.influencer_id === id);
    return {
      uses: credited.length,
      revenue: credited.reduce((sum, sale) => sum + Number(sale.sale_amount), 0),
      commission: credited.reduce((sum, sale) => sum + Number(sale.commission_amount), 0)
    };
  };

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div><h1>Influencers</h1><p>Create promo codes and monitor 20% commission performance.</p><p>Updated <time dateTime={today.toISOString().slice(0, 10)}>{today.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</time></p></div>
        <div className="admin-header-actions">
          <button className="admin-btn secondary" onClick={load}><SafeIcon icon={FiRefreshCw} /> Refresh</button>
          {activeTab === 'list' && <button className="admin-btn primary" onClick={() => setShowAdd(true)}><SafeIcon icon={FiPlus} /> Add Influencer</button>}
        </div>
      </div>
      <div className="admin-card" style={{ marginBottom: 16, padding: 8 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className={`admin-btn ${activeTab === 'list' ? 'primary' : 'secondary'}`} onClick={() => setActiveTab('list')}>Influencer List</button>
          <button className={`admin-btn ${activeTab === 'payouts' ? 'primary' : 'secondary'}`} onClick={() => setActiveTab('payouts')}>Influencer Payouts</button>
        </div>
      </div>
      {activeTab === 'payouts' && (
      <div className="admin-card" style={{ marginBottom: 16 }}>
        <div className="admin-card-header">
          <div>
            <h2>Monthly payout management</h2>
            <span className="small-text">Early and monthly payouts are deducted from the selected month’s balance.</span>
          </div>
          <div className="editor-field">
            <label>Month</label>
            <input type="month" value={payoutMonth} onChange={(event) => setPayoutMonth(event.target.value)} />
          </div>
        </div>
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead><tr><th>Influencer</th><th>Generated Sales</th><th>Commission</th><th>Paid</th><th>Balance</th><th>Payout</th></tr></thead>
            <tbody>
              {influencers.map((influencer) => {
                const payoutStats = payoutStatsFor(influencer.id);
                return (
                  <tr key={`payout-${influencer.id}`}>
                    <td><strong>{influencer.name}</strong></td>
                    <td>Rs. {payoutStats.revenue.toLocaleString()}</td>
                    <td>Rs. {payoutStats.commission.toLocaleString()}</td>
                    <td>Rs. {payoutStats.paid.toLocaleString()}</td>
                    <td><strong>Rs. {payoutStats.balance.toLocaleString()}</strong></td>
                    <td><button className="admin-action-btn primary" disabled={!payoutStats.balance} onClick={() => { setPayoutTarget(influencer); setPayoutMessage(''); }}>Record payout</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: 24 }}>
          <h2>Payout history</h2>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead><tr><th>Influencer</th><th>Type</th><th>Amount</th><th>Date</th><th>Notes</th></tr></thead>
              <tbody>
                {payouts.filter((payout) => payout.payout_month?.slice(0, 7) === payoutMonth).map((payout) => (
                  <tr key={payout.id}>
                    <td>{influencers.find((influencer) => influencer.id === payout.influencer_id)?.name || 'Influencer'}</td>
                    <td>{payout.payout_type === 'early' ? 'Early payout' : 'Monthly payout'}</td>
                    <td>Rs. {Number(payout.amount).toLocaleString()}</td>
                    <td>{new Date(payout.paid_at).toLocaleDateString()}</td>
                    <td>{payout.notes || '—'}</td>
                  </tr>
                ))}
                {!payouts.some((payout) => payout.payout_month?.slice(0, 7) === payoutMonth) && (
                  <tr><td colSpan="5">No payouts recorded for this month.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      )}
      {activeTab === 'list' && (
      <>
      {selectedIds.size > 0 && (
        <div className="batch-action-bar" style={{ margin: '0 28px 16px' }}>
          <span>{selectedIds.size} selected</span>
          <button className="admin-btn secondary batch-delete-btn" onClick={batchDelete}>
            <SafeIcon icon={FiTrash2} /> Delete Selected
          </button>
          <button className="admin-btn secondary" onClick={() => setSelectedIds(new Set())}>Clear</button>
        </div>
      )}
      <div className="admin-card">
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead><tr><th style={{ width: 40 }}><input type="checkbox" checked={influencers.length > 0 && selectedIds.size === influencers.length} onChange={toggleSelectAll} /></th><th>Influencer</th><th>Promo</th><th>Discount</th><th>Uses</th><th>Sales</th><th>Commission</th><th>Portal Access</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {influencers.map((influencer) => {
                const stats = statsFor(influencer.id);
                const portal = `${window.location.origin}/#/influencer/${influencer.portal_token}`;
                const isEditing = editingId === influencer.id;
                return (
                  <tr key={influencer.id} className={selectedIds.has(influencer.id) ? 'row-selected' : ''}>
                    <td><input type="checkbox" checked={selectedIds.has(influencer.id)} onChange={() => toggleSelect(influencer.id)} /></td>
                    <td>
                      {isEditing ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <input value={editForm.name} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} className="admin-inline-input" style={{ width: '100%' }} placeholder="Name" />
                          <input value={editForm.email} onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))} className="admin-inline-input" style={{ width: '100%' }} placeholder="WhatsApp number" />
                        </div>
                      ) : (
                        <><strong>{influencer.name}</strong><br /><span className="small-text">{influencer.email || 'No WhatsApp'}</span></>
                      )}
                    </td>
                    <td>
                      {isEditing ? (
                        <input value={editForm.promo_code} onChange={(e) => setEditForm((f) => ({ ...f, promo_code: e.target.value.toUpperCase() }))} className="admin-inline-input mono" style={{ textTransform: 'uppercase' }} />
                      ) : (
                        <strong>{influencer.promo_code}</strong>
                      )}
                    </td>
                    <td>
                      {isEditing ? (
                        <input type="number" value={editForm.discount_percent} onChange={(e) => setEditForm((f) => ({ ...f, discount_percent: e.target.value }))} className="admin-inline-input" style={{ width: 60 }} />
                      ) : (
                        <span>{influencer.discount_percent}%</span>
                      )}
                    </td>
                    <td>{stats.uses}</td>
                    <td>Rs. {stats.revenue.toLocaleString()}</td>
                    <td>Rs. {stats.commission.toLocaleString()}</td>
                    <td>
                      <span className="small-text">Password: {influencer.portal_password}</span><br />
                      <button className="admin-action-btn" style={{ marginTop: 6 }} onClick={() => copyAccessDetails(influencer, portal)}><SafeIcon icon={FiCopy} /> {copiedId === influencer.id ? 'Copied link + password' : 'Copy access details'}</button>
                    </td>
                    <td><button className={`admin-action-btn ${influencer.active ? 'primary' : 'danger'}`} onClick={() => toggleActive(influencer)}>{influencer.active ? 'Active' : 'Disabled'}</button></td>
                    <td>
                      <div className="admin-row-actions">
                        {isEditing ? (
                          <>
                            <button className="admin-icon-btn primary" onClick={() => saveEdit(influencer.id)} title="Save">
                              <SafeIcon icon={FiSave} />
                            </button>
                            <button className="admin-icon-btn" onClick={() => setEditingId(null)} title="Cancel">
                              <SafeIcon icon={FiX} />
                            </button>
                          </>
                        ) : (
                          <>
                            <button className="admin-icon-btn" onClick={() => startEdit(influencer)} title="Edit">
                              <SafeIcon icon={FiEdit2} />
                            </button>
                            <button className="admin-icon-btn danger" onClick={() => deleteInfluencer(influencer.id)} title="Delete">
                              <SafeIcon icon={FiTrash2} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      </>
      )}
      {showAdd && (
        <div className="admin-modal-overlay" onClick={() => setShowAdd(false)}>
          <div className="admin-modal admin-modal-sm" onClick={(event) => event.stopPropagation()}>
            <div className="admin-modal-header"><h2>Add Influencer</h2><button className="admin-icon-btn" onClick={() => setShowAdd(false)}><SafeIcon icon={FiX} /></button></div>
            <div className="admin-modal-body">
              {Object.entries({ name: 'Name', email: 'WhatsApp Number', promo_code: 'Promo Code', discount_percent: 'Customer Discount %', portal_password: 'Portal Password' }).map(([key, label]) => (
                <div className="editor-field" key={key}>
                  <label>{label}</label>
                  {key === 'portal_password' ? (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input type="text" value={form[key]} onChange={(event) => setForm((current) => ({ ...current, [key]: event.target.value }))} style={{ flex: 1 }} />
                      <button type="button" className="admin-btn secondary" onClick={() => setForm((current) => ({ ...current, portal_password: generatePassword() }))} style={{ whiteSpace: 'nowrap' }}>
                        <SafeIcon icon={FiKey} /> Generate
                      </button>
                    </div>
                  ) : (
                    <input type={key === 'discount_percent' ? 'number' : 'text'} value={form[key]} onChange={(event) => setForm((current) => ({ ...current, [key]: event.target.value }))} />
                  )}
                </div>
              ))}
            </div>
            <div className="admin-modal-footer"><button className="admin-btn secondary" onClick={() => setShowAdd(false)}>Cancel</button><button className="admin-btn primary" onClick={addInfluencer}><SafeIcon icon={FiSave} /> Save</button></div>
          </div>
        </div>
      )}
      {payoutTarget && (
        <div className="admin-modal-overlay" onClick={() => setPayoutTarget(null)}>
          <div className="admin-modal admin-modal-sm" onClick={(event) => event.stopPropagation()}>
            <div className="admin-modal-header"><h2>Record Payout</h2><button className="admin-icon-btn" onClick={() => setPayoutTarget(null)}><SafeIcon icon={FiX} /></button></div>
            <div className="admin-modal-body">
              <p style={{ marginBottom: 18 }}><strong>{payoutTarget.name}</strong> has Rs. {payoutStatsFor(payoutTarget.id).balance.toLocaleString()} available for {payoutMonth}.</p>
              <div className="editor-field">
                <label>Payout Type</label>
                <select value={payoutForm.payout_type} onChange={(event) => setPayoutForm((current) => ({ ...current, payout_type: event.target.value }))}>
                  <option value="monthly">Monthly payout</option>
                  <option value="early">Early payout</option>
                </select>
              </div>
              <div className="editor-field">
                <label>Amount</label>
                <input type="number" min="0.01" step="0.01" value={payoutForm.amount} onChange={(event) => setPayoutForm((current) => ({ ...current, amount: event.target.value }))} />
              </div>
              <div className="editor-field">
                <label>Notes</label>
                <textarea value={payoutForm.notes} onChange={(event) => setPayoutForm((current) => ({ ...current, notes: event.target.value }))} placeholder="Payment reference or notes" />
              </div>
              {payoutMessage && <p className="admin-login-error" style={{ marginTop: 14 }}>{payoutMessage}</p>}
            </div>
            <div className="admin-modal-footer"><button className="admin-btn secondary" onClick={() => setPayoutTarget(null)}>Cancel</button><button className="admin-btn primary" onClick={savePayout}><SafeIcon icon={FiSave} /> Confirm Payout</button></div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default AdminInfluencers;